/**
 * Acervo Canônico de Sites e Portais Mecânicos para Honda ADV 150 (K0W)
 * Padrão Fábio Akita - Boring Technology, Deep Modules & Contratos Estritos
 */

export type TechnicalWebsiteCategory =
  | "oficial_honda"
  | "epc_microfichas"
  | "manuais_servico"
  | "fabricantes_cvt"
  | "comunidades_foruns"
  | "ferramentas_normas";

export interface TechnicalWebsiteResource {
  id: string;
  title: string;
  category: TechnicalWebsiteCategory;
  categoryLabel: string;
  url: string;
  scope: "global" | "brasil" | "japao_asia" | "europa" | "eua";
  description: string;
  workshopUsage: string;
  featured: boolean;
}

export const ADV150_TECHNICAL_WEBSITES: TechnicalWebsiteResource[] = [
  // 1. Portais Oficiais Honda
  {
    id: "site-honda-motopub",
    title: "Honda MotoPub Global",
    category: "oficial_honda",
    categoryLabel: "Portais Oficiais Honda",
    url: "https://www.hondamotopub.com/",
    scope: "global",
    description:
      "Repositório oficial global da Honda Motor Co. para catálogos eletrônicos de peças (Código 18K0WLI1) e manuais do proprietário originais em PDF.",
    workshopUsage:
      "Download de manuais originais de fábrica e consulta à publicação oficial K0W sem intermediários.",
    featured: true,
  },
  {
    id: "site-honda-brasil-posvenda",
    title: "Honda Motos Brasil - Pós-Venda & Manuais",
    category: "oficial_honda",
    categoryLabel: "Portais Oficiais Honda",
    url: "https://www.honda.com.br/pos-venda/motos/manuais-e-guias",
    scope: "brasil",
    description:
      "Portal oficial da Moto Honda da Amazônia para download do Manual do Proprietário nacional em português (261 págs), plano de revisão e recalls.",
    workshopUsage:
      "Consulta às tabelas periódicas de revisão homologadas no Brasil e checagem de campanhas de segurança (recall por chassi).",
    featured: true,
  },
  {
    id: "site-myhonda-connect",
    title: "myHonda Connect (App Oficial)",
    category: "oficial_honda",
    categoryLabel: "Portais Oficiais Honda",
    url: "https://www.honda.com.br/pos-venda/motos",
    scope: "brasil",
    description:
      "Aplicativo oficial da Honda para acompanhamento do histórico de revisões em concessionárias Honda Dream e manual digital.",
    workshopUsage:
      "Consulta offline pelo celular aos planos de manutenção atrelados ao chassi da moto (VIN 9C2KF...).",
    featured: false,
  },

  // 2. Microfichas & Vistas Explodidas OEM
  {
    id: "site-bike-parts-honda",
    title: "Bike-Parts-Honda (Microfichas Oficiais)",
    category: "epc_microfichas",
    categoryLabel: "Microfichas & Vistas Explodidas OEM",
    url: "https://www.bike-parts-honda.com/",
    scope: "global",
    description:
      "Catálogo eletrônico interativo com vistas explodidas oficiais completas de todos os subsistemas de motor (E-1 a E-19) e chassi (F-1 a F-38).",
    workshopUsage:
      "Identificação visual exata de ordem de montagem de arruelas, retentores, O-rings e medidas métricas de todos os parafusos.",
    featured: true,
  },
  {
    id: "site-webike-japan-oem",
    title: "Webike Japan (OEM Parts Catalogue)",
    category: "epc_microfichas",
    categoryLabel: "Microfichas & Vistas Explodidas OEM",
    url: "https://japan.webike.net/HONDA/ADV150/14569/mtop/",
    scope: "japao_asia",
    description:
      "Catálogo oficial da linha japonesa e asiática da ADV 150 com vistas explodidas de alta resolução e consulta de part numbers originais.",
    workshopUsage:
      "Conferência de compatibilidade de peças entre ADV 150, PCX 150/160 e Click 150, além de cotação de peças genuínas japonesas.",
    featured: true,
  },
  {
    id: "site-partzilla",
    title: "Partzilla (Honda OEM Diagrams)",
    category: "epc_microfichas",
    categoryLabel: "Microfichas & Vistas Explodidas OEM",
    url: "https://www.partzilla.com/",
    scope: "eua",
    description:
      "Portal norte-americano de peças de reposição genuínas Honda com diagramas explodidos, fotos reais de itens e referências cruzadas.",
    workshopUsage:
      "Busca rápida de peças por código e conferência de códigos de substituição técnica de fábrica.",
    featured: false,
  },
  {
    id: "site-bikerzbits",
    title: "BikerzBits (Tailândia - Polo Fabril)",
    category: "epc_microfichas",
    categoryLabel: "Microfichas & Vistas Explodidas OEM",
    url: "https://bikerzbits.com/",
    scope: "japao_asia",
    description:
      "Exportador direto de peças genuínas OEM Honda direto da matriz de fabricação da ADV no Sudeste Asiático (A.P. Honda Tailândia).",
    workshopUsage:
      "Identificação de códigos originais de carenagens, peças de reposição mecânica e acessórios genuínos de fábrica.",
    featured: true,
  },
  {
    id: "site-cmsnl",
    title: "CMSNL (Consolidated Motor Spares)",
    category: "epc_microfichas",
    categoryLabel: "Microfichas & Vistas Explodidas OEM",
    url: "https://www.cmsnl.com/",
    scope: "europa",
    description:
      "Maior acervo europeu de microfichas e peças de reposição para motos japonesas, com mapeamento detalhado de peças descontinuadas.",
    workshopUsage:
      "Rastreamento de números de peças atualizados (superseded part numbers) e catálogo técnico europeu.",
    featured: false,
  },

  // 3. Manuais de Serviço & Metrologia
  {
    id: "site-cyclepedia",
    title: "Cyclepedia Press (Manuais de Oficina Online)",
    category: "manuais_servico",
    categoryLabel: "Manuais de Serviço & Metrologia",
    url: "https://www.cyclepedia.com/",
    scope: "global",
    description:
      "Base de manuais de oficina com fotografias passo a passo em alta definição, especificações de torque, limites de desgaste e esquemas elétricos coloridos.",
    workshopUsage:
      "Roteiros didáticos e procedimentos de bancada para desmontagem de motores e sistemas hidráulicos de scooters.",
    featured: true,
  },
  {
    id: "site-haynes-clymer",
    title: "Haynes / Clymer Manuals",
    category: "manuais_servico",
    categoryLabel: "Manuais de Serviço & Metrologia",
    url: "https://haynes.com/",
    scope: "global",
    description:
      "Manuais de oficina independentes elaborados a partir da desmontagem e remontagem completa de veículos por engenheiros mecânicos.",
    workshopUsage:
      "Árvores lógicas de diagnóstico de falhas, tabelas de torques e medições centesimais de componentes mecânicos.",
    featured: false,
  },
  {
    id: "site-manualslib-archives",
    title: "ManualsLib & Scribd Technical Archives",
    category: "manuais_servico",
    categoryLabel: "Manuais de Serviço & Metrologia",
    url: "https://www.manualslib.com/",
    scope: "global",
    description:
      "Acervos digitais onde manuais de serviço de concessionária da Honda ADV 150 (Shop Manual K0W - 211 págs) foram arquivados para consulta pública.",
    workshopUsage:
      "Consulta a diagramas unifilares de chicote elétrico PGM-FI e ABS, torques oficiais e tolerâncias de montagem de motor.",
    featured: false,
  },

  // 4. Fabricantes e Engenharia de Componentes Críticos
  {
    id: "site-malossi-tech",
    title: "Malossi Tech Center & Academy",
    category: "fabricantes_cvt",
    categoryLabel: "Fabricantes & Engenharia de Componentes",
    url: "https://www.malossi.com/",
    scope: "europa",
    description:
      "Centro técnico e de engenharia italiano especializado em transmissões CVT para scooters, termodinâmica e componentes de alto rendimento.",
    workshopUsage:
      "Estudos sobre rigidez de mola de contraste (constante K), dissipação térmica no cárter do CVT e comportamento de correias de transmissão.",
    featured: true,
  },
  {
    id: "site-polini-technical",
    title: "Polini Motori Technical",
    category: "fabricantes_cvt",
    categoryLabel: "Fabricantes & Engenharia de Componentes",
    url: "https://www.polini.com/",
    scope: "europa",
    description:
      "Fabricante europeu com documentação sobre dinâmica de abertura de variadores contínuos, rampas de roletes e calibragem de massas.",
    workshopUsage:
      "Compreensão do equilíbrio entre aceleração e velocidade final através da variação da gramatura de roletes no motor eSP.",
    featured: false,
  },
  {
    id: "site-dr-pulley",
    title: "Dr. Pulley / Union Material",
    category: "fabricantes_cvt",
    categoryLabel: "Fabricantes & Engenharia de Componentes",
    url: "https://drpulley.info/",
    scope: "global",
    description:
      "Pioneira na tecnologia de roletes deslizantes poligonais (Sliding Rollers) e guias autolubrificantes em compósito de nylon.",
    workshopUsage:
      "Entendimento da física de roletes deslizantes vs roletes cilíndricos tradicionais, prevenindo o desgaste de facetas nas canaletas.",
    featured: true,
  },
  {
    id: "site-bando-gates",
    title: "Bando Chemical & Gates Belts",
    category: "fabricantes_cvt",
    categoryLabel: "Fabricantes & Engenharia de Componentes",
    url: "https://bandousa.com/",
    scope: "global",
    description:
      "Fabricantes oficiais da correia de transmissão da Honda ADV 150 (23100-K97-T01). Manuais de engenharia de correias dentadas V-Belt.",
    workshopUsage:
      "Critérios metrológicos de inspeção de trincas na base do dente, desfiamento de cordões de tração e limite de largura de 18,5 mm.",
    featured: true,
  },

  // 5. Comunidades & Fóruns Técnicos
  {
    id: "site-advrider-scooters",
    title: 'ADVrider (Subfórum "Battle Scooters")',
    category: "comunidades_foruns",
    categoryLabel: "Comunidades & Fóruns Técnicos",
    url: "https://www.advrider.com/f/forums/battle-scooters.36/",
    scope: "global",
    description:
      "A maior e mais técnica comunidade de motociclismo de aventura do planeta. Megatópico exclusivo da Honda ADV 150 com milhares de relatos.",
    workshopUsage:
      "Resolução de problemas crônicos de suspensão Showa, durabilidade de correia sob poeira e relatos de desmontagem real de oficina.",
    featured: true,
  },
  {
    id: "site-honda-pcx-forum",
    title: "Honda PCX & ADV World Forum",
    category: "comunidades_foruns",
    categoryLabel: "Comunidades & Fóruns Técnicos",
    url: "https://hondapcx.org/",
    scope: "global",
    description:
      "Comunidade internacional dedicada aos modelos eSP da Honda (PCX e ADV 150). Tutoriais com fotos passo a passo e diagnósticos.",
    workshopUsage:
      "Guias de remoção de carenagens sem quebra de presilhas snap-fit e instalação de acessórios elétricos no chicote original.",
    featured: true,
  },
  {
    id: "site-scooterclube-brasil",
    title: "Fórum ScooterClube do Brasil",
    category: "comunidades_foruns",
    categoryLabel: "Comunidades & Fóruns Técnicos",
    url: "https://www.scooterclube.com.br/",
    scope: "brasil",
    description:
      "Pioneiro fórum técnico de scooters no Brasil, focado na realidade operacional das vias brasileiras e gasolina nacional com 27% de etanol.",
    workshopUsage:
      "Compartilhamento de histórico de pós-venda em concessionárias Honda Dream nacionais e dicas de manutenção preventiva no Brasil.",
    featured: false,
  },

  // 6. Normas Técnicas & Ferramentas JIS
  {
    id: "site-vessel-jis-norm",
    title: "Vessel Tools (Norma Técnica JIS B 1012)",
    category: "ferramentas_normas",
    categoryLabel: "Normas Técnicas & Ferramental JIS",
    url: "https://www.vessel.co.jp/english/",
    scope: "japao_asia",
    description:
      "Portal oficial da fabricante japonesa Vessel detalhando a norma JIS B 1012 para fixadores de cabeça cruciforme em motocicletas japonesas.",
    workshopUsage:
      "Compreensão de por que chaves Phillips comuns espanam parafusos japoneses e catálogo de chaves com certificação JIS +2 e +3.",
    featured: true,
  },
];

/**
 * Busca rápida de portais mecânicos por termo (nome, descrição, categoria ou aplicação)
 */
export function searchTechnicalWebsites(query: string): TechnicalWebsiteResource[] {
  const normalized = query.toLowerCase().trim();
  if (!normalized) return ADV150_TECHNICAL_WEBSITES;

  return ADV150_TECHNICAL_WEBSITES.filter(
    (item) =>
      item.title.toLowerCase().includes(normalized) ||
      item.description.toLowerCase().includes(normalized) ||
      item.workshopUsage.toLowerCase().includes(normalized) ||
      item.categoryLabel.toLowerCase().includes(normalized) ||
      item.category.toLowerCase().includes(normalized),
  );
}

/**
 * Retorna portais filtrados por categoria específica
 */
export function getWebsitesByCategory(
  category: TechnicalWebsiteCategory,
): TechnicalWebsiteResource[] {
  return ADV150_TECHNICAL_WEBSITES.filter((item) => item.category === category);
}

/**
 * Retorna os portais recomendados de destaque (featured)
 */
export function getFeaturedWebsites(): TechnicalWebsiteResource[] {
  return ADV150_TECHNICAL_WEBSITES.filter((item) => item.featured);
}
